<template>
  <div class="user-list-container">
    <h4>Listă Utilizatori și Ore Voluntariat</h4>
    
    <div v-if="loading">Se încarcă utilizatorii...</div>
    <div v-if="error" class="error">{{ error }}</div>
    
    <div v-if="message" class="message" :class="{ 'error': message.startsWith('Eroare') }">
      {{ message }}
    </div>  
    <div class="table-responsive-wrapper">
      <table v-if="users.length > 0" class="user-table">
        <thead>
          <tr>
            <th>Nume Complet</th>
            <th>Email</th>
            <th>Total Ore</th>
            <th>Rol (Schimbă)</th> </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.full_name }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.total_hours }} ore</td>
            <td>
              <div v-if="user.id === currentAdminId">
                <strong>{{ user.role }}</strong> (Contul tău)
              </div>

              <select 
                v-else
                :value="user.role" 
                @change="handleChangeRole(user, $event.target.value)"
                class="role-select"
              >
                <option v-for="role in roles" :key="role" :value="role">
                  {{ role }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import apiClient from '@/api.js';

// Variabile de stare
const users = ref([]);
const loading = ref(true);
const error = ref('');
const currentAdminId = ref(null); // NOU: ID-ul adminului logat
const message = ref(''); // NOU: Pentru feedback (ex: "Rol salvat!")

// NOU: Lista de roluri disponibile (din API-ul tău)
const roles = ['user', 'coordonator', 'admin'];

onMounted(async () => {
  // Preluăm ID-ul adminului curent din localStorage
  try {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData && userData.id) { // Am adăugat verificare pentru .id
      currentAdminId.value = userData.id;
    }
  } catch (e) {
    console.error("Nu am putut citi user-ul din localStorage", e);
  }

  // Preluăm lista de utilizatori (ca înainte)
  loading.value = true;
  try {
    const response = await apiClient.get('/admin/users');
    users.value = response.data;
  } catch (err) {
    console.error("Eroare la preluarea utilizatorilor:", err);
    error.value = 'Nu am putut încărca lista de utilizatori.';
  } finally {
    loading.value = false;
  }
});

// NOU: Funcția care se apelează la schimbarea rolului
const handleChangeRole = async (userToUpdate, newRole) => {
  message.value = 'Se salvează...';
  
  // Stocăm rolul vechi în caz de eroare
  const oldRole = userToUpdate.role; 

  try {
    // Apelăm endpoint-ul din API
    const response = await apiClient.put(
      `/admin/users/${userToUpdate.id}/role`, 
      { newRole: newRole } // Trimitem { newRole: '...' } așa cum se așteaptă API-ul
    );

    // Actualizăm datele local, fără a reîncărca pagina
    const userInList = users.value.find(u => u.id === userToUpdate.id);
    if (userInList) {
      userInList.role = response.data.user.role; // Folosim rolul returnat de API
    }
    
    message.value = `Rolul pentru ${userToUpdate.full_name} a fost salvat ca ${newRole}.`;
    
    // Ștergem mesajul după 3 secunde
    setTimeout(() => { message.value = ''; }, 3000);

  } catch (err) {
    console.error("Eroare la schimbarea rolului:", err);
    message.value = `Eroare: ${err.response?.data?.error || 'A apărut o eroare'}`;
    
    // Resetăm dropdown-ul la valoarea inițială în caz de eroare
    const userInList = users.value.find(u => u.id === userToUpdate.id);
    if (userInList) {
      userInList.role = oldRole; 
    }
  }
};
</script>

<style scoped>
.user-list-container {
  margin-top: 20px;
}
.user-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}
.user-table th, .user-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  vertical-align: middle; /* Aliniază dropdown-ul frumos */
}
.user-table th {
  background-color: #f2f2f2;
}
.role-select {
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  font-size: 0.95em;
  width: 100%; /* Ocupă tot spațiul celulei */
}
/* Mesaj de feedback */
.message {
  padding: 10px;
  background-color: #e0f0e8; /* Verde succes */
  color: #27ae60;
  border: 1px solid #27ae60;
  border-radius: 5px;
  margin-bottom: 15px;
}
.error {
  color: red;
}
/* Suprascriem culoarea mesajului dacă e eroare */
.message.error {
  background-color: #f8d7da;
  color: #C0392B;
  border-color: #C0392B;
}

/* ########################################## */
/* ## STILURI PENTRU TABELE PE MOBIL (NOU)
/* ########################################## */
@media (max-width: 768px) {
  .table-responsive-wrapper {
    width: 100%;
    overflow-x: auto; /* Asta e magia: adaugă scroll orizontal */
    -webkit-overflow-scrolling: touch; /* Pentru scroll fin pe iOS */
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .user-table {
    /* Forțăm tabelul să aibă o lățime minimă */
    min-width: 600px; 
  }
}
</style>